import type { ReactNode } from 'react';
import {
  Bug,
  CheckCircle2,
  ClipboardList,
  FileText,
  FlaskConical,
  MessageSquare,
  Pill,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';

import { DebugPanel } from '@/features/copilot-debug/ui/DebugPanel';
import { DiagnoseWizard } from '@/features/diagnose-form';
import { DosespotActionWizard } from '@/features/dosespot-action-form/ui/DosespotActionWizard';
import { FollowUpsTab } from '@/features/follow-ups';
import { LabOrderWizard } from '@/features/lab-order-form';
import { PaCompletedRxView } from '@/features/pa-completed-rx-view';
import { PriorAuthActionWizard } from '@/features/prior-auth-form';
import { RxWizard } from '@/features/rx-form';
import {
  AI_DEBUG_OVERLAY_DEFAULT_SIZE,
  DIAGNOSE_WIZARD_OVERLAY_DEFAULT_SIZE,
  FOLLOW_UPS_OVERLAY_DEFAULT_SIZE,
  LAB_ORDER_WIZARD_OVERLAY_DEFAULT_SIZE,
  RX_WIZARD_OVERLAY_DEFAULT_SIZE,
} from '@/shared/config';
import type { Size } from '@/shared/ui/primitives';

import type { CopilotOverlayId, CopilotOverlayPayloads } from '@/shared/stores/ui';

export interface CopilotOverlayDefinition<K extends CopilotOverlayId = CopilotOverlayId> {
  id: K;
  headerIcon: LucideIcon;
  headerIconLabel: string;
  defaultSize: Size;
  render: (userId: string, payload: CopilotOverlayPayloads[K]) => ReactNode;
}

type CopilotOverlayDefinitions = {
  [K in CopilotOverlayId]: CopilotOverlayDefinition<K>;
};

export const COPILOT_OVERLAY_DEFINITIONS: CopilotOverlayDefinitions = {
  rxWizard: {
    id: 'rxWizard',
    headerIcon: Pill,
    headerIconLabel: 'Prescription',
    defaultSize: {
      width: RX_WIZARD_OVERLAY_DEFAULT_SIZE.width,
      height: RX_WIZARD_OVERLAY_DEFAULT_SIZE.height,
    },
    render: (userId, prefill) => <RxWizard userId={userId} prefill={prefill} />,
  },
  labOrderWizard: {
    id: 'labOrderWizard',
    headerIcon: FlaskConical,
    headerIconLabel: 'Lab Order',
    defaultSize: {
      width: LAB_ORDER_WIZARD_OVERLAY_DEFAULT_SIZE.width,
      height: LAB_ORDER_WIZARD_OVERLAY_DEFAULT_SIZE.height,
    },
    render: (userId, prefill) => <LabOrderWizard userId={userId} prefill={prefill} />,
  },
  diagnoseWizard: {
    id: 'diagnoseWizard',
    headerIcon: Stethoscope,
    headerIconLabel: 'Diagnosis',
    defaultSize: {
      width: DIAGNOSE_WIZARD_OVERLAY_DEFAULT_SIZE.width,
      height: DIAGNOSE_WIZARD_OVERLAY_DEFAULT_SIZE.height,
    },
    render: (userId, prefill) => <DiagnoseWizard userId={userId} prefill={prefill} />,
  },
  followUps: {
    id: 'followUps',
    headerIcon: MessageSquare,
    headerIconLabel: 'Follow Ups',
    defaultSize: {
      width: FOLLOW_UPS_OVERLAY_DEFAULT_SIZE.width,
      height: FOLLOW_UPS_OVERLAY_DEFAULT_SIZE.height,
    },
    render: () => <FollowUpsTab />,
  },
  dosespotAction: {
    id: 'dosespotAction',
    headerIcon: ClipboardList,
    headerIconLabel: 'DoseSpot Action',
    defaultSize: { width: 520, height: 840 },
    render: (_userId, prefill) => <DosespotActionWizard prefill={prefill} />,
  },
  priorAuthAction: {
    id: 'priorAuthAction',
    headerIcon: FileText,
    headerIconLabel: 'Prior Authorization',
    defaultSize: { width: 560, height: 820 },
    render: (userId, prefill) => <PriorAuthActionWizard patientUserId={userId} prefill={prefill} />,
  },
  priorAuthCompletedRx: {
    id: 'priorAuthCompletedRx',
    headerIcon: CheckCircle2,
    headerIconLabel: 'PA Approved Rx',
    defaultSize: { width: 520, height: 720 },
    render: (_userId, prefill) => <PaCompletedRxView prefill={prefill} />,
  },
  aiDebug: {
    id: 'aiDebug',
    headerIcon: Bug,
    headerIconLabel: 'AI Debug',
    defaultSize: {
      width: AI_DEBUG_OVERLAY_DEFAULT_SIZE.width,
      height: AI_DEBUG_OVERLAY_DEFAULT_SIZE.height,
    },
    render: () => <DebugPanel />,
  },
};
